import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { PolicyKey } from '@kirin/api-client/contracts/policies';
import {
  PolicyExceptionAction,
  SKILL_VERSION_RULE_FIELDS,
  skillIdentityName,
  type SkillVersion,
} from '@kirin/api-client/contracts/policy-exceptions';
import { createGroupPolicyException } from '@kirin/api-client/api/policy-exceptions';
import { ApiError } from '@kirin/api-client/errors';
import { queryKeys } from './queryKeys';
import {
  canonicalEcosystem,
  packageKey,
  type PackageIdentity,
} from './usePackageListActions';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

/** A security group's own choice for an Inventory item. */
export type InventoryGroupChoice = 'block' | 'allow';

/**
 * How one kind of Inventory item maps onto group policy-exception rules.
 * Module constants, so the actions keep one identity across renders.
 */
export interface InventoryGroupRuleItem<T> {
  policyKey: PolicyKey;
  /** In-flight key: the item's identity, folded as the matchers fold it. */
  key: (item: T) => string;
  ruleObject: (item: T) => Record<string, unknown>;
  label: (item: T) => string;
  /** The list to refetch after a change; awaited so `isPending` covers it. */
  listQueryKey: (organizationId: string) => QueryKey;
  track: (item: T, change: InventoryGroupChoice) => void;
}

/**
 * Every action resolves, never rejects: a failure is reported with a toast
 * inside the hook.
 */
export interface InventoryGroupRuleActions<T> {
  /**
   * Store the group's Allow or Block. The backend keeps one group rule per
   * item, so this replaces the group's previous choice on it.
   */
  setChoice: (item: T, choice: InventoryGroupChoice) => Promise<void>;
  /** True from the click until the list has refetched. */
  isPending: (item: T) => boolean;
}

/**
 * A skill as a group choice names it: one row, so one version (KIR-5174). Two
 * same-named skills whose SKILL.md differs are two rows, and a choice on one
 * must leave the other alone.
 */
export interface SkillGroupItem {
  name: string;
  source: string;
  project: string | null;
  /** The row's version, or null while it is not known yet. */
  version: SkillVersion | null;
  displayName?: string;
}

function normalizeProject(project: unknown): string | null {
  return typeof project === 'string' ? project.trim() || null : null;
}

/** Hashes are lowercase hex; a blank one means none. */
function normalizeHash(hash: unknown): string | null {
  return typeof hash === 'string' ? hash.trim().toLowerCase() || null : null;
}

export const skillGroupRuleItem: InventoryGroupRuleItem<SkillGroupItem> = {
  policyKey: PolicyKey.SkillsManagement,
  key: skill =>
    [
      skillIdentityName(skill.name),
      skill.source.trim().toLowerCase(),
      normalizeProject(skill.project) ?? '',
      skill.version ? skill.version.scheme : '',
      normalizeHash(skill.version?.hash) ?? '',
    ].join('\u0000'),
  // `project` is always sent: the schema requires it, as an explicit null for
  // skills with no project.
  ruleObject: skill => {
    // A rule naming no version would block every same-named skill, the bug
    // KIR-5174 fixed for the organization. Refuse rather than over-block.
    const hash = normalizeHash(skill.version?.hash);
    if (skill.version === null || hash === null) {
      throw new Error(
        'Its SKILL.md has not been reported yet. Try again after the next scan.'
      );
    }
    return {
      name: skill.name,
      source: skill.source,
      project: normalizeProject(skill.project),
      [SKILL_VERSION_RULE_FIELDS[skill.version.scheme]]: hash,
    };
  },
  label: skill => skill.displayName ?? skill.name,
  listQueryKey: () => queryKeys.skills.all,
  track: (skill, change) =>
    trackEvent('skill_list_change', {
      scope: 'group',
      source: 'skills_page',
      change,
      // Bucketed like the organization event: a plugin name is free text.
      skill_source: skill.source.startsWith('plugin:')
        ? 'plugin'
        : skill.source,
    }),
};

export const packageGroupRuleItem: InventoryGroupRuleItem<PackageIdentity> = {
  policyKey: PolicyKey.PackagesManagement,
  key: packageKey,
  ruleObject: pkg => ({ name: pkg.name, ecosystem: pkg.ecosystem }),
  label: pkg => pkg.name,
  listQueryKey: organizationId => [
    'agent-command-events',
    organizationId,
    'aggregated-packages',
  ],
  track: (pkg, change) =>
    trackEvent('package_list_change', {
      scope: 'group',
      source: 'packages_page',
      change,
      ecosystem: canonicalEcosystem(pkg.ecosystem),
    }),
};

/**
 * KIR-4701: Allow / Block an Inventory skill or package for one security
 * group, through the group policy-exception routes. A no-op without a group:
 * the organization scope keeps its own hooks. The row's state comes from the
 * server-computed `listState` and `enforcementSource`; the way back to the
 * organization's choices is the group-wide reset (`useGroupScopeReset`).
 */
export function useInventoryGroupRuleActions<T>(
  organizationId: string,
  securityGroupId: string | null,
  item: InventoryGroupRuleItem<T>
): InventoryGroupRuleActions<T> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inFlight = useRef(new Set<string>());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const run = useCallback(
    async (
      target: T,
      change: InventoryGroupChoice,
      /** Resolves whether anything was written; only a write is tracked. */
      work: (groupId: string) => Promise<boolean>
    ): Promise<void> => {
      if (!securityGroupId) return;
      // Per scope: one hook serves every group the tab switches between, and a
      // write still running in one must not mark the row pending in another.
      const key = `${securityGroupId}\u0000${item.key(target)}`;
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setBusy(new Set(inFlight.current));
      try {
        if (await work(securityGroupId)) item.track(target, change);
      } catch (error) {
        toast({
          title: `Could not ${change} ${item.label(target)}`,
          description: getApiErrorMessage(error, 'Please try again.'),
          variant: 'destructive',
        });
      } finally {
        // Refetch on failure too: another admin may have changed the row.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.policyExceptions.group(
              organizationId,
              securityGroupId,
              item.policyKey
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: item.listQueryKey(organizationId),
          }),
        ]).catch(() => undefined);
        inFlight.current.delete(key);
        setBusy(new Set(inFlight.current));
      }
    },
    [item, organizationId, queryClient, securityGroupId, toast]
  );

  const setChoice = useCallback(
    (target: T, choice: InventoryGroupChoice) =>
      run(target, choice, async groupId => {
        try {
          await createGroupPolicyException(
            apiClient,
            organizationId,
            groupId,
            item.policyKey,
            {
              action:
                choice === 'block'
                  ? PolicyExceptionAction.Block
                  : PolicyExceptionAction.Allow,
              ruleObject: item.ruleObject(target),
            }
          );
          return true;
        } catch (error) {
          // A 409 means the group already holds this exact choice: a no-op.
          if (error instanceof ApiError && error.status === 409) return false;
          throw error;
        }
      }),
    [item, organizationId, run]
  );

  const isPending = useCallback(
    (target: T) =>
      busy.has(`${securityGroupId ?? ''}\u0000${item.key(target)}`),
    [busy, item, securityGroupId]
  );

  return useMemo(() => ({ setChoice, isPending }), [setChoice, isPending]);
}
